import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/AxiosInstance";

const PAGE_SIZE = 20;

const ActorTracker = () => {
  const [actors, setActors] = useState([]);
  const [trackedActors, setTrackedActors] = useState([]);
  const [trackedActorIds, setTrackedActorIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Track expanded summaries
  const [expandedActors, setExpandedActors] = useState({});

  // Fetch tracked actors once
  useEffect(() => {
    const fetchTrackedActors = async () => {
      try {
        const res = await axiosInstance.get(`/insights/actors/tracked/`);
        const tracked = res.data || [];
        setTrackedActors(tracked);
        setTrackedActorIds(new Set(tracked.map((a) => a.stix_id)));
      } catch (err) {
        console.error("Failed to fetch tracked actors", err);
        setMessage("Failed to load tracked actors.");
      }
    };
    fetchTrackedActors();
  }, []);

  // Fetch paginated actors
  useEffect(() => {
    const fetchActors = async () => {
      setLoading(true);
      setMessage("");
      try {
        const params = new URLSearchParams();
        if (search.trim()) params.append("q", search.trim());
        params.append("page", page);
        params.append("page_size", PAGE_SIZE);

        const res = await axiosInstance.get(`/insights/actors/search/?${params.toString()}`);
        const all = res.data.results || [];
        const untracked = all.filter((a) => !trackedActorIds.has(a.stix_id));
        setActors(untracked);
        setTotalPages(Math.ceil(res.data.count / PAGE_SIZE));
      } catch (err) {
        console.error("Failed to fetch actors", err);
        setMessage("Error loading actor list.");
      }
      setLoading(false);
    };

    fetchActors();
  }, [search, page, trackedActorIds]);

  const toggleTracked = async (actor) => {
    const { stix_id, name, domain } = actor;
    const isTracked = trackedActorIds.has(stix_id);
    try {
      if (isTracked) {
        await axiosInstance.delete(`/insights/actors/tracked/untrack/${stix_id}/`);
        setTrackedActorIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(stix_id);
          return newSet;
        });
        setTrackedActors((prev) => prev.filter((a) => a.stix_id !== stix_id));
        setActors((prev) => [...prev, { ...actor, tracked: false }]);
        setMessage(`Stopped tracking ${name}.`);
      } else {
        await axiosInstance.post(`/insights/actors/tracked/add/`, { stix_id, name, domain });
        setTrackedActorIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(stix_id);
          return newSet;
        });
        setTrackedActors((prev) => [...prev, { ...actor, tracked: true }]);
        setActors((prev) => prev.filter((a) => a.stix_id !== stix_id));
        setMessage(`Started tracking ${name}.`);
      }
    } catch (err) {
      console.error("Error updating tracking", err);
      setMessage("Failed to update tracking status.");
    }
  };

  const renderActorCard = (actor) => {
    const isExpanded = expandedActors[actor.stix_id] || false;
    const toggleSummary = () => {
      setExpandedActors((prev) => ({
        ...prev,
        [actor.stix_id]: !prev[actor.stix_id],
      }));
    };

    const isLong = actor.description && actor.description.length > 200;
    const summaryText = actor.description
      ? isExpanded || !isLong
        ? actor.description
        : `${actor.description.slice(0, 200)}...`
      : "No description.";

    return (
      <div key={actor.stix_id} className="mb-4 p-4 border border-gray-700 rounded bg-gray-800 text-white">
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold break-words max-w-[70%]">{actor.name}</span>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={trackedActorIds.has(actor.stix_id)}
              onChange={() => toggleTracked(actor)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-purple-600 relative
              after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border
              after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all
              peer-checked:after:translate-x-full peer-checked:after:border-white" />
            <span className="ml-3 text-sm text-gray-200">
              {trackedActorIds.has(actor.stix_id) ? "Tracking" : "Not Tracking"}
            </span>
          </label>
        </div>

        <div className="mt-2 text-sm text-gray-300 whitespace-pre-wrap">
          {summaryText}
          {isLong && (
            <button
              onClick={toggleSummary}
              className="block text-purple-400 mt-1 text-xs hover:underline"
            >
              {isExpanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>

        {actor.aliases?.length > 0 && (
          <p className="mt-1 text-xs text-gray-400 italic">Aliases: {actor.aliases.join(", ")}</p>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Track Threat Actors</h2>
      <p className="text-sm text-gray-400 mb-4">
        Search and select threat actors to follow. These will be used to support various features in the app such as threat actor heatmap generation.
      </p>

      <input
        type="text"
        placeholder="Search by name or alias"
        value={search}
        onChange={(e) => {
          setPage(1);
          setSearch(e.target.value);
        }}
        className="px-3 py-2 rounded bg-gray-700 text-white w-full mb-6"
      />

      {!search.trim() && trackedActors.length > 0 && (
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-green-400 mb-2">Currently Tracking</h3>
        {trackedActors.map(renderActorCard)}
      </div>
    )}

      <h3 className="text-lg font-semibold text-purple-400 mb-2">
        {search.trim() ? "Search Results" : "All Threat Actors"}
      </h3>

      {loading ? (
        <p className="text-center text-gray-400">Loading...</p>
      ) : actors.length === 0 ? (
        <p className="text-center text-gray-400">No actors found.</p>
      ) : (
        actors.map(renderActorCard)
      )}

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-6">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
          >
            Previous
          </button>
          <span className="text-gray-300">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-3 py-1 rounded bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
          >
            Next
          </button>
        </div>
      )}

      {message && <p className="mt-4 text-sm text-green-400">{message}</p>}
    </div>
  );
};

export default ActorTracker;
